import { Navigate, useParams } from 'react-router-dom'
import { useListComponentsQuery } from '../api/authoring'
import { routes } from '../model/routes'
import { SITE_TYPE } from '../model/website'
import { TenantSites } from './TenantSites'
import { Loading, QueryError } from '../ui/QueryState'

/**
 * Where a reader who named only a tenant lands.
 *
 * **A tenant holding one site opens that site.** A list of one is not a
 * choice, and making somebody click through it every time they open the
 * tool is the kind of ceremony an information architecture accretes when
 * each screen is designed on its own. Nearly every tenant is one
 * customer with one site, so this is the common path rather than a
 * special case.
 *
 * A tenant with two sites gets the list, and so does a tenant with none
 * — an empty list says something true and a redirect would have nowhere
 * to go.
 *
 * This replaces the remembered mode, which used to decide this one
 * redirect from a value in browser storage. That was the single place
 * where what an address meant depended on something other than the
 * address; what replaces it is a fact about the tenant, read from the
 * platform, that every browser agrees on.
 *
 * The listing is the one authoring read served from the projection, so
 * it lags — but only into the *list*, which is the safe direction: a
 * tenant that has just had its second site created might still be
 * redirected into its first for a moment, and never the reverse.
 */
export function TenantLanding() {
  const { tenant = '' } = useParams()
  const { data, error, isLoading } = useListComponentsQuery({ tenant, type: SITE_TYPE })

  if (isLoading) return <Loading label="Listing this tenant's sites…" />
  if (error) return <QueryError error={error} title="Could not list this tenant's sites" />

  const sites = data?.components ?? []
  // Exactly one, and no more pages behind it: a cursor means the
  // listing continued, so "one" is only what this page held.
  if (sites.length === 1 && !data?.cursor) return <Navigate to={routes.site(tenant, sites[0].id)} replace />

  return <TenantSites />
}
